import type { TemplateContext } from "@/shared/utils/templateReplacement";

const MAX_ITERATIONS = 500;

function isTruthy(context: TemplateContext, varName: string): boolean {
  const value = context[varName];
  return value !== undefined && value !== null && value.trim() !== "";
}

/**
 * Processes Handlebars-style conditional blocks in a template string.
 *
 * Supports {{#if varName}}...{{/if}} and {{#if varName}}...{{else}}...{{/if}}.
 * Handles arbitrary nesting by iteratively resolving the innermost blocks first.
 *
 * Must be called BEFORE replaceTemplateVariables so that removed blocks
 * don't leave behind unresolved {{variable}} placeholders.
 */
export function processConditionalBlocks(template: string, context: TemplateContext): string {
  let result = template;

  // Innermost if/else: matches {{#if X}}...{{else}}...{{/if}} with no nested {{#if}} inside
  const innerIfElse =
    /\{\{#if\s+(\w+)\}\}((?:(?!\{\{#if\b)[\s\S])*?)\{\{else\}\}((?:(?!\{\{#if\b)[\s\S])*?)\{\{\/if\}\}/;

  // Innermost if-only: matches {{#if X}}...{{/if}} with no nested {{#if}} inside
  const innerIfOnly = /\{\{#if\s+(\w+)\}\}((?:(?!\{\{#if\b)[\s\S])*?)\{\{\/if\}\}/;

  for (let i = 0; i < MAX_ITERATIONS; i++) {
    // Try if/else first (more specific pattern)
    const elseMatch = innerIfElse.exec(result);
    if (elseMatch) {
      const [fullMatch, varName, ifContent, elseContent] = elseMatch;
      const replacement = isTruthy(context, varName) ? ifContent : elseContent;
      result = result.replace(fullMatch, replacement);
      continue;
    }

    // Then try if-only
    const ifMatch = innerIfOnly.exec(result);
    if (ifMatch) {
      const [fullMatch, varName, ifContent] = ifMatch;
      const replacement = isTruthy(context, varName) ? ifContent : "";
      result = result.replace(fullMatch, replacement);
      continue;
    }

    break;
  }

  return result;
}
